import { useEffect, useState } from "react";
import axios from "axios";

// react-boostrap
import { Container, Card, Col, Row, Spinner } from "react-bootstrap";

import {
  MdPerson,
  MdGroups,
  MdSchool,
  MdAssignment,
  MdPsychology,
  MdFactCheck,
  MdUpload,
  MdBarChart,
  MdStar,
  MdEventAvailable,
  MdCancel,
  MdMenuBook,
  MdCampaign,
  MdNotifications,
  MdClose,
  MdCalendarToday,
} from "react-icons/md";

import img1 from "../../assests/dashboard/1.png";
import img2 from "../../assests/dashboard/2.png";
import img3 from "../../assests/dashboard/3.png";
import img4 from "../../assests/dashboard/4.png";
import img5 from "../../assests/dashboard/5.png";
import img6 from "../../assests/dashboard/6.png";
import img7 from "../../assests/dashboard/7.png";
import img8 from "../../assests/dashboard/8.png";
import img9 from "../../assests/dashboard/9.png";
import useApi from "../../API/useApi";
import LoadingGreen from "../loading/LoadingGreen";
import useUserData from "../../custom_hooks/useUserData";
import useSelection from "antd/es/table/hooks/useSelection";
import { useSelector } from "react-redux";
import { BASE_URL } from "../../API/webserviceUrl";

// ---------------------------------------------------------------------
// NOTE: every sample/fallback data block that used to live here
// (leadInfo, submissionsOverview, attendanceStatus,
// supportNotificationsSeed, topGroupingStatus, dayWiseData,
// programDays, todaySchedule, announcements) has been removed. All of
// it is now sourced from state populated by API calls below. Only
// pure formatting/config constants remain.
// ---------------------------------------------------------------------

// Today's date, formatted for display in the Attendance card.
const todaysDate = new Date().toLocaleDateString("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

// Palette — one place for every color so the page reads as one
// designed system.
const palette = {
  ink: "#16a34a",
  navy: "#1B2A56",
  indigo: "#4C51BF",
  teal: "#0E9594",
  amber: "#F2A93B",
  amberSoft: "#FCF1DF",
  green: "#2FA84F",
  greenSoft: "#E7F6EB",
  red: "#DC2626",
  redSoft: "#FDE8E8",
  slate: "#8A93A6",
  bg: "#F6F7FB",
};

const heroGradient = `linear-gradient(135deg, #16a34a 0%, #1d733d  55%, #1d733d  100%)`;

// Light, flat card used for anything sitting OUTSIDE the dark hero
// panel — this is the "clean white card" language from the
// reference screens (icon circle + number/label, or icon circle +
// title), used consistently across stat cards and info cards.
const flatCard = {
  background: "#fff",
  border: "1px solid #eef0f4",
  borderRadius: 14,
  boxShadow: "0 1px 3px rgba(22,33,62,0.05)",
};

// Small pastel icon badge — the light-green circle behind icons
// that shows up everywhere in the reference screens.
const iconBadge = (
  bg = palette.greenSoft,
  color = palette.green,
  size = 42,
) => ({
  width: size,
  height: size,
  borderRadius: 12,
  background: bg,
  color,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: size * 0.42,
  flexShrink: 0,
});

// Small helper — initials from a full name, for the trainer avatar.
const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

// Formats an ISO date string ("2025-08-28") into a short display
// label ("28 Aug"), used for the API-driven Program Detail day tabs.
const formatDayLabel = (dateStr: string) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// Formats "03:00:00" / "06:00:00" (as returned by /lesson_plan) into
// "03:00 - 06:00".
const formatTimeRange = (start?: string, end?: string) => {
  const fmt = (t?: string) => (t ? t.slice(0, 5) : "");
  if (!start && !end) return "";
  return `${fmt(start)} - ${fmt(end)}`;
};

// A ring-shaped progress indicator used for the Reviewed tile.
// SVG only, no chart library — keeps the tile lightweight.
const ProgressRing = ({
  value,
  total,
  color,
  trackColor,
  textColor = "#1a1a1a",
  size = 64,
}: any) => {
  const stroke = 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = total > 0 ? Math.min(value / total, 1) : 0;
  const offset = circumference * (1 - pct);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={trackColor}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={size * 0.3}
        fontWeight="700"
        fill={textColor}
      >
        {value}
      </text>
    </svg>
  );
};

// Small metadata item used inside the green hero banner — icon in a
// translucent white circle, small caps label, value underneath.
// Mirrors the "DATE OF TRAINING / LOCATION / COORDINATORS" row
// style from the reference screen.
const HeroMetaItem = ({ Icon, label, value }: any) => (
  <div className="d-flex align-items-center gap-2">
    <span
      className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
      style={{
        width: 30,
        height: 30,
        borderRadius: 8,
        background: "rgba(255,255,255,0.18)",
        color: "#fff",
        fontSize: "0.9rem",
      }}
    >
      <Icon />
    </span>
    <div className="min-w-0">
      <div
        style={{
          fontSize: "0.62rem",
          letterSpacing: "0.08em",
          color: "rgba(255,255,255,0.65)",
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        className="text-truncate"
        style={{ fontSize: "0.85rem", fontWeight: 700, color: "#fff" }}
        title={value}
      >
        {value}
      </div>
    </div>
  </div>
);

// Flat white stat card — icon circle + big number + label, laid out
// in a row. This is the exact card language used for the stat
// tiles under the green banner in the reference screen.
const StatCard = ({
  icon,
  iconBg,
  iconColor,
  value,
  label,
  sub,
  indicatorColor, // optional — pass a color to show a small status dot next to the value
}: any) => (
  <div className="h-100 d-flex align-items-center gap-3 p-3" style={flatCard}>
    <span style={iconBadge(iconBg, iconColor, 46)}>{icon}</span>
    <div className="min-w-0">
      <div className="d-flex align-items-center gap-2">
        {indicatorColor && (
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: indicatorColor,
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ fontSize: "1rem", fontWeight: 600, color: "#1a1a1a" }}>
          {value}
        </div>
      </div>
      <div
        style={{ fontSize: "0.78rem", color: palette.slate, fontWeight: 600 }}
      >
        {label}
      </div>
      {sub && (
        <div style={{ fontSize: "0.72rem", color: palette.slate }}>{sub}</div>
      )}
    </div>
  </div>
);

// Notification prompt row — used inside the expanded Support
// Notifications panel. Shows title/detail/time plus a dismiss button.
const NotificationRow = ({ notification, onDismiss }: any) => (
  <div
    className="d-flex align-items-start justify-content-between gap-2"
    style={{
      padding: "10px 10px",
      borderRadius: 10,
      borderBottom: "1px solid #f1f2f6",
    }}
  >
    <div className="d-flex align-items-start gap-2 min-w-0">
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: palette.amber,
          flexShrink: 0,
          marginTop: 6,
        }}
      />
      <div className="min-w-0">
        <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#1e293b" }}>
          {notification.title}
        </div>
        {notification.detail && (
          <div
            style={{
              fontSize: "0.76rem",
              color: palette.slate,
              marginTop: 2,
            }}
          >
            {notification.detail}
          </div>
        )}
        <div style={{ fontSize: "0.68rem", color: "#94a3b8", marginTop: 4 }}>
          {notification.time}
        </div>
      </div>
    </div>
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onDismiss(notification.id);
      }}
      className="btn btn-sm d-inline-flex align-items-center gap-1"
      style={{
        fontSize: "0.68rem",
        fontWeight: 700,
        color: palette.slate,
        background: palette.bg,
        border: "none",
        borderRadius: 8,
        padding: "4px 8px",
        flexShrink: 0,
      }}
    >
      <MdClose size={12} />
      Dismiss
    </button>
  </div>
);

// Mode → color map for the Today's Schedule timeline dots/badges.
const scheduleModeColor = {
  Online: palette.green,
  "In person": palette.amber,
} as any;

const formatTime = (t: any) => {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let hour = parseInt(hStr, 10);
  if (Number.isNaN(hour)) return "";
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${mStr} ${ampm}`;
};
// Today's Schedule row — time / title on the left, a small colored
// timeline dot, and a mode badge on the right. Styled with the same
// flatCard/palette language as the rest of the dashboard.
const ScheduleRow = ({ item, isLast }: any) => {
  const dotColor = scheduleModeColor[item.mode] || palette.slate;
  return (
    <div
      className="d-flex align-items-start gap-3"
      style={{ paddingBottom: isLast ? 0 : 16 }}
    >
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: dotColor,
          flexShrink: 0,
          marginTop: 6,
          boxShadow: `0 0 0 3px ${dotColor}22`,
        }}
      />
      <div className="d-flex align-items-center justify-content-between flex-grow-1 min-w-0 gap-2">
        <div className="min-w-0">
          <div
            style={{
              fontSize: "0.72rem",
              color: palette.slate,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            {`${formatTime(item.time?.split("-")[0])} - ${formatTime(item.time?.split("-")[1])}`}
          </div>
          <div
            style={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "#1f2937",
              marginTop: 2,
            }}
          >
            {item.title}
          </div>
          <div
            style={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "#1f2937",
              marginTop: 2,
            }}
          >
            {item.description}
          </div>
        </div>
        <span
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: dotColor,
            background: `${dotColor}1a`,
            borderRadius: 999,
            padding: "4px 10px",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {item.mode}
        </span>
      </div>
    </div>
  );
};

const LMSDashboard = () => {
  const logos = [img9, img1, img2, img3, img4, img5, img6, img7, img8];
  const [pageLoading, setPageLoading] = useState(true);
  const token = localStorage.getItem("token");
  const { coruse_cohort }: any = useUserData();

  let respProgram = coruse_cohort;
  // Program Detail — which day tab is active in the bottom section.
  // Starts empty until real lesson-plan days load from the API.
  const [activeProgramDay, setActiveProgramDay] = useState<string>("");
  const course_data = useSelector(
    (state: any) => state?.themeSetting?.cohort_data,
  );

  // ---------------------------------------------------------------------
  // course_data (redux: themeSetting.cohort_data) may arrive as:
  //  - a JSON string (e.g. from localStorage-backed redux state)
  //  - an already-parsed object (e.g. set directly via dispatch)
  //  - null / undefined (not set yet)
  // JSON.parse() blows up on anything that isn't a string (it coerces
  // objects to the literal text "[object Object]" first, which is
  // invalid JSON). Guard against all three cases here instead of
  // assuming it's always a string.
  // ---------------------------------------------------------------------
  let coruse_cohorts: any = {};
  try {
    if (typeof course_data === "string") {
      coruse_cohorts = course_data ? JSON.parse(course_data) : {};
    } else if (course_data && typeof course_data === "object") {
      coruse_cohorts = course_data;
    }
  } catch (err) {
    console.error("Failed to parse cohort_data from redux:", err);
    coruse_cohorts = {};
  }

  // Support notifications — expand/collapse state plus the live list
  // so items can be dismissed, now populated from /get_support_notifications.
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const [pendingCandidates, setPendingCandidates] = useState<any[]>([]);
  const [allCandidatesAssigned, setAllCandidatesAssigned] = useState<boolean | null>(null);
  const [groupingLoading, setGroupingLoading] = useState(true);

  const dismissNotification = (id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  useEffect(() => {
    const timer = setTimeout(() => setPageLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  // **************************** Support Team
  const {
    data: SupportTeamData,
    loading: SupportTeamLoading,
    error: SupportTeamError,
    callApi: getAdvanseData,
  } = useApi("/get_batches", {
    method: "POST",
    autoCall: false,
  });

  useEffect(() => {
    getAdvanseData({
      employee_id: "EM4168250624144420",
      school_code: 102,
    });
  }, []);

  useEffect(() => {
    console.log(SupportTeamData?.data, "SupportTeamData");
  }, [SupportTeamData]);

  // ---------------------------------------------------------------------
  // /get-dashboard-details — student/lead name, training dates, venue,
  // coordinators, day-of-training count, notification count, TOP
  // submission due date. Feeds the hero panel + a couple of stat cards.
  // ---------------------------------------------------------------------
  const [dashboardDetails, setDashboardDetails] = useState<any>(null);

  useEffect(() => {
    const fetchDashboardDetails = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/get-dashboard-details`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.data?.success) {
          setDashboardDetails(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard details", err);
      }
    };

    fetchDashboardDetails();
  }, []);

  // ---------------------------------------------------------------------
  // /get_support_notifications — live notifications feed for the Lead.
  // NOTE: this endpoint name is assumed — point it at your real one if
  // it's named differently.
  // ---------------------------------------------------------------------
  useEffect(() => {

    console.log("Fetched notification")
    const fetchSupportNotifications = async () => {
      try {
        setNotificationsLoading(true);
        const res = await axios.get(
          `${BASE_URL}/get_support_notifications`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setNotifications(res.data?.success ? res.data.data || [] : []);
      } catch (err) {
        console.error("Failed to fetch support notifications", err);
        setNotifications([]);
      } finally {
        setNotificationsLoading(false);
      }
    };

    fetchSupportNotifications();
  }, []);

  // ---------------------------------------------------------------------
  // /get_attendance_status — whether today's attendance has been
  // submitted by the Lead yet. NOTE: endpoint name assumed.
  // ---------------------------------------------------------------------
  const [attendanceStatus, setAttendanceStatus] = useState<{
    submittedToday: boolean | null;
  }>({ submittedToday: null });

  useEffect(() => {
    const fetchAttendanceStatus = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/get_attendance_status`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.data?.success) {
          setAttendanceStatus({
            submittedToday: !!res.data.data?.submittedToday,
          });
        }
      } catch (err) {
        console.error("Failed to fetch attendance status", err);
      }
    };

    fetchAttendanceStatus();
  }, []);

  // ---------------------------------------------------------------------
  // /get_top_grouping_status — whether the Lead has finished grouping
  // participants for TOP submission. NOTE: endpoint name assumed.
  // ---------------------------------------------------------------------
  const [topGroupingStatus, setTopGroupingStatus] = useState<{
    completed: boolean | null;
  }>({ completed: null });

  const [pending, setPending] = useState(0);

  useEffect(() => {
    const fetchTopGroupingStatus = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/get_top_grouping_status`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.data?.success) {

          //@ts-ignore
          console.log("Response :", res)

          //@ts-ignore
          setPending(res?.pending_count);
        }
      } catch (err) {
        console.error("Failed to fetch TOP grouping status", err);
      }
    };

    fetchTopGroupingStatus();
  }, []);

  // ---------------------------------------------------------------------
  // /getLessonPlanDays — the list of dates that have a lesson plan.
  // Drives the Program Detail day tabs.
  // ---------------------------------------------------------------------
  const [lessonPlanDays, setLessonPlanDays] = useState<string[]>([]);

  const fetchPendingSupport = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/get-pending-support`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data?.success) {
        console.log("Pending support:", res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch pending support", err);
    }
  };

  useEffect(() => {
    const fetchLessonPlanDays = async () => {
      try {
        const res = await axios.post(
          `${BASE_URL}/getLessonPlanDays`,
          {
            class_name: respProgram?.class || coruse_cohorts?.class,
            section: respProgram?.section || coruse_cohorts?.section,
            level: respProgram?.level || coruse_cohorts?.level,
            combination_code:
              respProgram?.combination_code || coruse_cohorts?.combination_code,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.data?.success) {
          const days = (res.data.data || [])
            .map((d: any) => d.day)
            .filter(Boolean); // drop the { day: null } placeholder entry
          setLessonPlanDays(days);
          if (days.length > 0) {
            setActiveProgramDay(days[0]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch lesson plan days", err);
      }
    };
    fetchLessonPlanDays();
  }, [
    respProgram?.class,
    respProgram?.section,
    coruse_cohorts?.class,
    coruse_cohorts?.section,
  ]);

  // ---------------------------------------------------------------------
  // /lesson_plan — the actual sessions for whichever day is selected.
  // Re-runs whenever a day tab is clicked (activeProgramDay changes),
  // but only once real lesson-plan days have loaded. Drives BOTH
  // Program Detail and Today's Schedule below.
  // ---------------------------------------------------------------------
  const [lessonPlanSessions, setLessonPlanSessions] = useState<any[]>([]);
  const [lessonPlanLoading, setLessonPlanLoading] = useState(false);

  useEffect(() => {
    if (lessonPlanDays.length === 0 || !activeProgramDay) return;

    const fetchLessonPlan = async () => {
      try {
        setLessonPlanLoading(true);
        const res = await axios.post(
          `${BASE_URL}/lesson_plan`,
          {
            class_name: respProgram?.class || coruse_cohorts?.class,
            section: respProgram?.section || coruse_cohorts?.section,
            level: respProgram?.level || coruse_cohorts?.level,
            combination_code:
              respProgram?.combination_code || coruse_cohorts?.combination_code,
            planned_on: activeProgramDay,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setLessonPlanSessions(res.data?.success ? res.data.data || [] : []);
      } catch (err) {
        console.error("Failed to fetch lesson plan", err);
        setLessonPlanSessions([]);
      } finally {
        setLessonPlanLoading(false);
      }
    };

    fetchLessonPlan();
  }, [activeProgramDay, lessonPlanDays]);

  useEffect(() => {
    const fetchNotInGroup = async () => {
      try {
        setGroupingLoading(true);
        const res = await axios({
          method: "get",
          url: `${BASE_URL}/candidates/not-in-group`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            class_name: respProgram?.class || coruse_cohorts?.class,
            section: respProgram?.section || coruse_cohorts?.section,
            level: respProgram?.level || coruse_cohorts?.level,
            combination_code:
              respProgram?.combination_code || coruse_cohorts?.combination_code,
          },
        });

        if (res.data?.success) {
          setPendingCandidates(res.data.data || []);
          setAllCandidatesAssigned(!!res.data.allCandidatesAssigned);
        }
      } catch (err) {
        console.error("Failed to fetch not-in-group candidates", err);
        setPendingCandidates([]);
        setAllCandidatesAssigned(null);
      } finally {
        setGroupingLoading(false);
      }
    };

    fetchNotInGroup();
  }, [
    respProgram?.class,
    respProgram?.section,
    coruse_cohorts?.class,
    coruse_cohorts?.section,
  ]);

  // Icons swapped in for the emoji this section used to carry —
  // each maps to what the trainer actually does in that step.
  const aboutItems = [
    {
      Icon: MdAssignment,
      title: "Manage Lesson Plans",
      desc: "Create, view, and update structured lesson plans for your sessions.",
    },
    {
      Icon: MdPsychology,
      title: "Conduct Training Sessions",
      desc: "Use the integrated training interface to deliver lessons and engage with participants.",
    },
    {
      Icon: MdFactCheck,
      title: "Mark Attendance",
      desc: "Track and update attendance records for every session.",
    },
    {
      Icon: MdUpload,
      title: "Manage Submissions",
      desc: "Review and evaluate assignments submitted by participants.",
    },
    {
      Icon: MdBarChart,
      title: "Handle Assessments",
      desc: "Assign new assessments, view submissions, and manage evaluation seamlessly.",
    },
    {
      Icon: MdStar,
      title: "Ensure Quality Learning",
      desc: "Provide structured sessions, maintain lesson flow, and ensure an interactive learning experience.",
    },
  ];

  useEffect(() => {
    const fetchTopGroupingStatus = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/get-pending-support`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        // @ts-ignore
        if (res?.data?.success) {
          console.log("reach id block", res?.data)
          //@ts-ignore
          setPending(res?.data?.pending_count ?? 0);
        }
      } catch (err) {
        console.error("Failed to fetch TOP grouping status", err);
      }
    };

    fetchTopGroupingStatus();
  }, []);
  // ---- Derived display values -----------------------------------------
  // Everything below now comes straight from dashboardDetails — no more
  // static leadInfo/venue fallback strings.
  const displayLeadInfo = {
    name: dashboardDetails?.studentName || "",
    cohort: dashboardDetails?.cohort || "",
    course: dashboardDetails?.course || dashboardDetails?.program || "",
  };
  const displayVenue = dashboardDetails?.location || "";
  const displayCoordinators =
    dashboardDetails?.coordinators
      ?.map((c: any) => c.name)
      .filter(Boolean)
      .join(", ") || "—";
  const displayDateOfTraining = dashboardDetails?.dateOfTraining?.display;
  const displayDayOfTraining = dashboardDetails?.dayOfTraining
    ? `Day ${dashboardDetails.dayOfTraining.current} of ${dashboardDetails.dayOfTraining.total}`
    : null;
  const displayNotificationCount =
    dashboardDetails?.notifications?.new ?? notifications.length;
  const displayTopSubmission = dashboardDetails?.topSubmission;

  // Program Detail — driven entirely by /getLessonPlanDays + /lesson_plan.
  const dayTabs = lessonPlanDays.map((d) => ({
    key: d,
    label: formatDayLabel(d),
  }));

  // const activeSessions = lessonPlanSessions.map((s: any, idx: number) => ({
  //   unit: s.subunit_lsnpln_id || `U${idx + 1}`,
  //   title: s.subunit_desc || s.topic,
  //   description: s.description,
  //   lead: s.emp_name || "TBD",
  //   duration: formatTimeRange(s.start_time, s.end_time),
  //   venue: s.venue,
  //   ...s,
  // }));

  const activeSessions = lessonPlanSessions
    .filter(
      (s: any, idx: number, arr: any[]) =>
        !s.lesson_plan_id ||
        idx ===
        arr.findIndex((x: any) => x.lesson_plan_id === s.lesson_plan_id),
    )
    .map((s: any, idx: number) => ({
      unit: s.subunit_lsnpln_id || `U${idx + 1}`,
      title: s.subunit_desc || s.topic,
      description: s.description,
      lead: s.emp_name || "TBD",
      duration: formatTimeRange(s.start_time, s.end_time),
      venue: s.venue,
      display_time: `${formatTime(lessonPlanSessions[0].start_time)}-${formatTime(lessonPlanSessions[lessonPlanSessions.length - 1].end_time)}`,
      ...s,
    }));

  // ---------------------------------------------------------------------
  // Today's Schedule — driven by the SAME /lesson_plan response
  // (lessonPlanSessions) used by Program Detail above. Each session
  // becomes one row: time from start_time/end_time, title from
  // subunit_desc (falling back to topic), and a mode badge derived
  // from whether a venue is present ("In person" vs "Online").
  // ---------------------------------------------------------------------
  const displayTodaySchedule = lessonPlanSessions.map((s: any) => ({
    time: formatTimeRange(s.start_time, s.end_time),
    title: s.subunit_desc || s.topic,
    mode: s.venue ? "In person" : "Online",
    ...s,
  }));

  // Grouping status label/color — treat "still loading" (null) as its
  // own state instead of silently defaulting to Pending.
  const groupingLoadings = topGroupingStatus.completed === null;
  const groupingLabel = topGroupingStatus.completed ? "Completed" : "Pending";
  const groupingColor = groupingLoading
    ? palette.slate
    : topGroupingStatus.completed
      ? palette.green
      : palette.red;
  const groupingSoftColor = groupingLoadings
    ? palette.bg
    : topGroupingStatus.completed
      ? palette.greenSoft
      : palette.redSoft;

  // Attendance status label/color — same "loading" treatment.
  const attendanceLoading = attendanceStatus.submittedToday === null;
  const attendanceLabel = attendanceStatus.submittedToday
    ? "Submitted"
    : "Attendance Pending";

  return (
    <div className="page-wrapper" style={{ background: palette.bg }}>
      <div className="content">
        {pageLoading ? (
          <LoadingGreen height="85vh" />
        ) : (
          <div
            id="content-page"
            className="content-inner dashboard pt-0 pt-md-4"
          >
            <Container fluid>
              <Row className="justify-content-center">
                <Col lg="12">
                  <Card.Body className="px-1 px-md-2">
                    {/* ============================================================
                          HERO PANEL — logos + welcome, PLUS a compact
                          metadata row (lead / cohort / course) styled
                          like the reference screen's green banner.
                          The heavier stat content now lives in flat
                          white cards BELOW the panel, not stacked
                          inside it as dark glass tiles.
                      ============================================================ */}
                    <div
                      className="p-3 p-md-4 mb-3"
                      style={{ background: heroGradient, borderRadius: 22 }}
                    >
                      {/* Logo row + welcome */}
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                        <div className="text-center">
                          <div
                            style={{
                              fontSize: "0.72rem",
                              letterSpacing: "0.14em",
                              color: "rgba(255,255,255,0.65)",
                              fontWeight: 600,
                              textAlign: "left",
                            }}
                          >
                            LEAD DASHBOARD
                          </div>
                          <h1
                            style={{
                              color: "#fff",
                              fontWeight: 800,
                              fontSize: "1.5rem",
                              margin: 0,
                            }}
                          >
                            Welcome back, {displayLeadInfo.name.split(" ")[0]}
                          </h1>
                        </div>
                      </div>

                      {/* Compact metadata row — lead / cohort / course,
                          same visual language as the reference banner's
                          date / location / coordinators row */}
                      <div className="d-flex flex-wrap gap-4 pt-2 border-top border-white border-opacity-25">
                        <HeroMetaItem
                          Icon={MdPerson}
                          label="Lead Trainer"
                          value={displayLeadInfo.name}
                        />
                        {displayLeadInfo.cohort && (
                          <HeroMetaItem
                            Icon={MdGroups}
                            label="Current Cohort"
                            value={displayLeadInfo.cohort}
                          />
                        )}
                        {displayLeadInfo.course && (
                          <HeroMetaItem
                            Icon={MdSchool}
                            label="Course"
                            value={displayLeadInfo.course}
                          />
                        )}
                        {displayVenue && (
                          <HeroMetaItem
                            Icon={MdSchool}
                            label="Venue"
                            value={displayVenue}
                          />
                        )}
                        <HeroMetaItem
                          Icon={MdEventAvailable}
                          label="Coordinators"
                          value={displayCoordinators}
                        />
                        {displayDateOfTraining && (
                          <HeroMetaItem
                            Icon={MdCalendarToday}
                            label="Date of Training"
                            value={displayDateOfTraining}
                          />
                        )}
                        {displayDayOfTraining && (
                          <HeroMetaItem
                            Icon={MdBarChart}
                            label="Day of Training"
                            value={displayDayOfTraining}
                          />
                        )}
                      </div>
                    </div>

                    {/* Flat white stat cards — sit below the green
                        panel, matching the reference screen's stat
                        row instead of being glass tiles inside the
                        dark banner.
                        Card 1: Pending Support Notifications
                                (click to expand individual prompts)
                        Card 2: TOP Submission Grouping Status
                        Card 3: Attendance by Lead */}
                    <Row className="g-3 mb-4">
                      <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        style={{ position: "relative" }}
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setShowNotifications((prev) => !prev)}
                          className="h-100 d-flex align-items-center gap-3 p-3"
                          style={{ ...flatCard, cursor: "pointer" }}
                        >
                          <span
                            style={iconBadge(
                              palette.amberSoft,
                              palette.amber,
                              46,
                            )}
                          >
                            <MdNotifications />
                          </span>
                          <div className="min-w-0">
                            <div
                              style={{
                                fontSize: "1rem",
                                fontWeight: 600,
                                color: "#1a1a1a",
                              }}
                            >
                              {pending}
                            </div>
                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: palette.slate,
                                fontWeight: 600,
                              }}
                            >
                              Pending Support Notifications
                            </div>
                          </div>
                        </div>

                        {/* Expanded prompts panel — one row per
                            notification, each dismissible on its own. */}
                        {showNotifications && (
                          <div
                            style={{
                              ...flatCard,
                              position: "absolute",
                              top: "calc(100% + 8px)",
                              left: 0,
                              right: 0,
                              zIndex: 20,
                              maxHeight: 320,
                              overflowY: "auto",
                              padding: 8,
                            }}
                          >
                            {notificationsLoading ? (
                              <div className="d-flex align-items-center gap-2 p-3">
                                <Spinner animation="border" size="sm" />
                                <span
                                  style={{
                                    fontSize: "0.82rem",
                                    color: palette.slate,
                                  }}
                                >
                                  Loading…
                                </span>
                              </div>
                            ) : notifications.length === 0 ? (
                              <div
                                className="text-center p-3"
                                style={{
                                  fontSize: "0.82rem",
                                  color: palette.slate,
                                }}
                              >
                                You're all caught up.
                              </div>
                            ) : (
                              notifications.map((n) => (
                                <NotificationRow
                                  key={n.id}
                                  notification={n}
                                  onDismiss={dismissNotification}
                                />
                              ))
                            )}
                          </div>
                        )}
                      </Col>

                      <Col xs={12} sm={6} lg={3}>
                        <div
                          className="h-100 d-flex align-items-center gap-3 p-3"
                          style={flatCard}
                        >
                          <span
                            style={iconBadge(
                              groupingSoftColor,
                              groupingColor,
                              46,
                            )}
                          >
                            <MdGroups />
                          </span>
                          <div className="min-w-0">
                            <div className="d-flex align-items-center gap-2">
                              <span
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  background: groupingColor,
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "1rem",
                                  fontWeight: 600,
                                  color: "#1a1a1a",
                                }}
                              >
                                {groupingLabel}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: palette.slate,
                                fontWeight: 600,
                              }}
                            >
                              TOP Submission Grouping Status
                            </div>
                            {displayTopSubmission && (
                              <div
                                style={{
                                  fontSize: "0.72rem",
                                  color: palette.slate,
                                }}
                              >
                                Due: {displayTopSubmission}
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>

                      <Col xs={12} sm={6} lg={3}>
                        <StatCard
                          icon={
                            attendanceStatus.submittedToday ? (
                              <MdEventAvailable />
                            ) : (
                              <MdCancel />
                            )
                          }
                          iconBg={
                            attendanceStatus.submittedToday
                              ? palette.greenSoft
                              : palette.amberSoft
                          }
                          iconColor={
                            attendanceStatus.submittedToday
                              ? palette.green
                              : palette.amber
                          }
                          value={attendanceLabel}
                          label="Attendance by Lead"
                          sub={todaysDate}
                        />
                      </Col>
                      <Col xs={12} sm={6} lg={3}>
                        <StatCard
                          icon={<MdGroups />}
                          iconBg={groupingSoftColor}
                          iconColor={groupingColor}
                          value={groupingLabel}
                          label="Grouping Status Indicator"
                          indicatorColor={groupingColor}
                        />
                      </Col>
                    </Row>

                    {/* ============================================================
                          PROGRAM DETAIL (left)  ·  TODAY'S SCHEDULE (right)
                          Program Detail: day tabs from /getLessonPlanDays,
                          each showing that day's sessions from
                          /lesson_plan, with a unit badge, lead and
                          duration.
                          Today's Schedule: driven by the same
                          /lesson_plan response (lessonPlanSessions).
                      ============================================================ */}
                    <Row className="g-3 mb-4 align-items-stretch">
                      {/* Program Detail */}
                      <Col md={6}>
                        <div
                          style={{ ...flatCard, padding: 0 }}
                          className="h-100"
                        >
                          <div
                            className="d-flex flex-wrap align-items-center justify-content-between gap-3 p-3 p-md-4"
                            style={{ borderBottom: "1px solid #eef0f4" }}
                          >
                            <div className="d-flex align-items-center">
                              <span
                                style={iconBadge(
                                  palette.greenSoft,
                                  palette.green,
                                  40,
                                )}
                              >
                                <MdMenuBook />
                              </span>
                              <h5
                                className="mb-0 ms-2"
                                style={{ fontWeight: 700, color: "#1a1a1a" }}
                              >
                                Program Detail
                              </h5>
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                              {dayTabs.map((day) => (
                                <button
                                  key={day.key}
                                  type="button"
                                  onClick={() => setActiveProgramDay(day.key)}
                                  className="btn btn-sm rounded-pill fw-semibold"
                                  style={{
                                    background:
                                      activeProgramDay === day.key
                                        ? palette.ink
                                        : palette.bg,
                                    color:
                                      activeProgramDay === day.key
                                        ? "#fff"
                                        : palette.slate,
                                    border: "none",
                                    padding: "6px 18px",
                                  }}
                                >
                                  {day.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="p-3 p-md-4">
                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: palette.slate,
                                fontWeight: 600,
                                marginBottom: 12,
                              }}
                            >
                              {activeProgramDay
                                ? formatDayLabel(activeProgramDay)
                                : ""}
                            </div>

                            {lessonPlanLoading && (
                              <div className="d-flex align-items-center gap-2 py-3">
                                <Spinner animation="border" size="sm" />
                                <span
                                  style={{
                                    fontSize: "0.82rem",
                                    color: palette.slate,
                                  }}
                                >
                                  Loading sessions…
                                </span>
                              </div>
                            )}

                            {!lessonPlanLoading &&
                              activeSessions.length === 0 && (
                                <div
                                  style={{
                                    fontSize: "0.82rem",
                                    color: palette.slate,
                                  }}
                                >
                                  No sessions found for this day.
                                </div>
                              )}

                            {!lessonPlanLoading &&
                              activeSessions.map(
                                (session: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="d-flex align-items-start justify-content-between gap-3"
                                    style={{
                                      background: palette.bg,
                                      border: "1px solid #eef0f4",
                                      borderRadius: 12,
                                      padding: "12px 16px",
                                      marginTop: idx === 0 ? 0 : 10,
                                    }}
                                  >
                                    <div className="d-flex align-items-start gap-3 min-w-0">
                                      {/* <span
                                        style={{
                                          flexShrink: 0,
                                          background: palette.greenSoft,
                                          color: palette.green,
                                          fontWeight: 700,
                                          fontSize: "0.7rem",
                                          borderRadius: 8,
                                          padding: "4px 9px",
                                          marginTop: 2,
                                        }}
                                      >
                                        {session.topic}
                                      </span> */}
                                      <div className="min-w-0">
                                        <div
                                          style={{
                                            fontSize: "0.92rem",
                                            fontWeight: 700,
                                            color: "#1e293b",
                                          }}
                                        >
                                          {session.topic}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "0.78rem",
                                            color: palette.slate,
                                            marginTop: 2,
                                          }}
                                        >
                                          {session.unit}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "0.78rem",
                                            color: palette.slate,
                                            marginTop: 2,
                                          }}
                                        >
                                          Lead: {session.lead}
                                        </div>
                                        {/* {session.description && (
                                          <div
                                            style={{
                                              fontSize: "0.78rem",
                                              color: "#94a3b8",
                                              marginTop: 4,
                                              lineHeight: 1.4,
                                            }}
                                          >
                                            {session.description}
                                          </div>
                                        )} */}
                                        {session.venue && (
                                          <div
                                            style={{
                                              fontSize: "0.72rem",
                                              color: "#94a3b8",
                                              marginTop: 2,
                                            }}
                                          >
                                            Venue: {session.venue}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {session.display_time && (
                                      <span
                                        style={{
                                          fontSize: "0.78rem",
                                          fontWeight: 700,
                                          color: "#475569",
                                          background: "#fff",
                                          border: "1px solid #e2e8f0",
                                          borderRadius: 999,
                                          padding: "4px 12px",
                                          flexShrink: 0,
                                          marginTop: 2,
                                        }}
                                      >
                                        {session.display_time}
                                      </span>
                                    )}
                                  </div>
                                ),
                              )}
                          </div>
                        </div>
                      </Col>

                      {/* Today's Schedule */}
                      <Col md={6}>
                        <div
                          style={{ ...flatCard, padding: 0 }}
                          className="h-100"
                        >
                          <div
                            className="d-flex align-items-center p-3 p-md-4"
                            style={{ borderBottom: "1px solid #eef0f4" }}
                          >
                            <span
                              style={iconBadge(
                                palette.greenSoft,
                                palette.green,
                                40,
                              )}
                            >
                              <MdCalendarToday />
                            </span>
                            <h5
                              className="mb-0 ms-2"
                              style={{ fontWeight: 700, color: "#1a1a1a" }}
                            >
                              Today's Schedule
                            </h5>
                          </div>

                          <div className="p-3 p-md-4">
                            {lessonPlanLoading && (
                              <div className="d-flex align-items-center gap-2 py-3">
                                <Spinner animation="border" size="sm" />
                                <span
                                  style={{
                                    fontSize: "0.82rem",
                                    color: palette.slate,
                                  }}
                                >
                                  Loading schedule…
                                </span>
                              </div>
                            )}

                            {!lessonPlanLoading &&
                              displayTodaySchedule.length === 0 && (
                                <div
                                  style={{
                                    fontSize: "0.82rem",
                                    color: palette.slate,
                                  }}
                                >
                                  No sessions scheduled.
                                </div>
                              )}

                            {!lessonPlanLoading &&
                              displayTodaySchedule.map((item, idx) => (
                                <ScheduleRow
                                  key={idx}
                                  item={item}
                                  isLast={
                                    idx === displayTodaySchedule.length - 1
                                  }
                                />
                              ))}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* ============================================================
                          ANNOUNCEMENTS — moved to the bottom of the
                          dashboard, below Program Detail. Currently
                          disabled; wire it to a real announcements feed
                          and pass that data in before re-enabling.
                      ============================================================ */}
                    {/* <div
                      className="mb-4"
                      style={{
                        ...flatCard,
                        padding: 0,
                      }}
                    >
                      <div
                        className="d-flex align-items-center p-3 p-md-4"
                        style={{ borderBottom: "1px solid #eef0f4" }}
                      >
                        <span
                          style={iconBadge(
                            palette.amberSoft,
                            palette.amber,
                            40,
                          )}
                        >
                          <MdCampaign />
                        </span>
                        <h5
                          className="mb-0 ms-2"
                          style={{ fontWeight: 700, color: "#1a1a1a" }}
                        >
                          Announcements
                        </h5>
                      </div>
                      <div
                        className="p-2 p-md-3"
                        style={{ height: "300px", overflow: "scroll" }}
                      >
                        {announcements.map((item, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-start justify-content-between gap-3"
                            style={{
                              padding: "10px 12px",
                              borderRadius: 10,
                            }}
                          >
                            <div className="d-flex align-items-start gap-2 min-w-0">
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  background: palette.ink,
                                  flexShrink: 0,
                                  marginTop: 6,
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "0.9rem",
                                  fontWeight: 600,
                                  color: "#1f2937",
                                }}
                              >
                                {item.title}
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: "0.72rem",
                                color: palette.slate,
                                flexShrink: 0,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.date}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div> */}
                  </Card.Body>
                </Col>
              </Row>
            </Container>
          </div>
        )}
      </div>
      {/* Footer */}
      {/* <div className="footer d-smflex d-flex align-items-center justify-content-center border-top bg-white p-3 pb-0 pt-2">
        <p className="mb-0">
           Organized by IHFC, IIT 
        </p>
      </div> */}
    </div>
  );
};

export default LMSDashboard;
